import React from 'react';
import { motion } from 'framer-motion';
import { Package, ArrowRight, UtensilsCrossed, User, MapPin, Calendar, Clock } from 'lucide-react';
import BottomNavigation from '../../components/common/BottomNavigation';
import { useApp } from '../../context/AppContext';
import { useOrder } from '../../context/OrderContext';
import { useAuth } from '../../context/AuthContext';
import { useTable } from '../../context/TableContext';

const CustomerHistory: React.FC = () => {
  const { setCurrentScreen } = useApp();
  const { getCustomerOrders, orders: allOrders } = useOrder();
  const { user } = useAuth();
  const { selectedTable } = useTable();
  
  // Get orders for the current customer
  const customerId = user?.customerId || user?.id;
  const orders = user && customerId ? getCustomerOrders(customerId) : [];
  
  // Debug logging
  console.log('🔍 CustomerHistory: Current user:', user);
  console.log('🔍 CustomerHistory: Customer ID:', customerId);
  console.log('🔍 CustomerHistory: All orders available:', allOrders.length);
  console.log('🔍 CustomerHistory: Orders found:', orders.length);
  console.log('🔍 CustomerHistory: Orders:', orders);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'delivered':
        return 'bg-green-900 text-green-400';
      case 'shipped':
        return 'bg-blue-900 text-blue-400';
      case 'pending':
        return 'bg-yellow-900 text-yellow-400';
      case 'cancelled':
        return 'bg-red-900 text-red-400';
      default:
        return 'bg-slate-900 text-slate-400';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'delivered':
        return 'Served';
      case 'shipped':
        return 'Ready';
      case 'pending':
        return 'Preparing';
      case 'cancelled':
        return 'Cancelled';
      default:
        return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  const handleOrderClick = (orderId: string) => {
    console.log('🔍 CustomerHistory: Order clicked:', orderId);
    console.log('🔍 CustomerHistory: Available orders:', orders.map(o => ({ id: o.id, status: o.status })));
    
    // Store the selected order ID in localStorage so CustomerTrack can access it
    localStorage.setItem('selectedOrderId', orderId);
    console.log('🔍 CustomerHistory: Stored order ID in localStorage:', orderId);
    
    console.log('🔍 CustomerHistory: Navigating to customer-track screen');
    setCurrentScreen('customer-track');
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true
    });
  };

  const OrderItem: React.FC<{ order: any; index: number }> = ({ order, index }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      className="food-card cursor-pointer hover:shadow-xl transition-all duration-200 hover:border-orange-500"
      onClick={() => handleOrderClick(order.id)}
    >
      <div className="space-y-4">
        {/* Order Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center">
              <Package size={20} className="text-white" />
            </div>
            <div>
              <h3 className="text-white font-bold text-lg">Order #{order.id}</h3>
              <div className="flex items-center space-x-2 text-gray-400 text-sm">
                <Calendar size={14} />
                <span>{formatDate(order.date)}</span>
                <Clock size={14} className="ml-2" />
                <span>{formatTime(order.date)}</span>
              </div>
            </div>
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(order.status)}`}>
            {getStatusLabel(order.status)}
          </span>
        </div>

        {/* Customer and Table Info */}
        <div className="bg-slate-800 rounded-lg p-3 space-y-2">
          <div className="flex items-center space-x-2 text-gray-300">
            <User size={16} className="text-orange-400" />
            <span className="font-medium">Customer:</span>
            <span className="text-white font-semibold">{order.customerName || user?.name || 'N/A'}</span>
          </div>
          
          {order.tableNumber && (
            <div className="flex items-center space-x-2 text-gray-300">
              <MapPin size={16} className="text-orange-400" />
              <span className="font-medium">Table:</span>
              <span className="text-white font-semibold">
                {order.tableNumber}
                {order.tableLocation && (
                  <span className="text-gray-400 ml-1">({order.tableLocation})</span>
                )}
              </span>
            </div>
          )}
        </div>

        {/* Order Items */}
        <div className="space-y-2">
          <h4 className="text-gray-400 text-sm font-medium">Items Ordered:</h4>
          <div className="space-y-1">
            {order.items?.slice(0, 3).map((item: any, idx: number) => (
              <div key={idx} className="flex justify-between items-center text-sm">
                <span className="text-gray-300">
                  {item.quantity}x {item.productName}
                </span>
                <span className="text-orange-400 font-medium">
                  ${(item.price * item.quantity).toFixed(2)}
                </span>
              </div>
            ))}
            {order.items?.length > 3 && (
              <div className="text-gray-500 text-xs">
                +{order.items.length - 3} more items...
              </div>
            )}
          </div>
        </div>

        {/* Order Total and Action */}
        <div className="flex items-center justify-between pt-3 border-t border-gray-700">
          <div>
            <p className="text-orange-400 font-bold text-xl">${order.amount.toFixed(2)}</p>
            <p className="text-gray-500 text-xs">Total Amount</p>
          </div>
          <div className="flex items-center text-orange-400 text-sm font-medium">
            <span>View Details</span>
            <ArrowRight size={16} className="ml-1" />
          </div>
        </div>
      </div>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-slate-900 to-gray-900 pb-20">
      {/* Header */}
      <div className="sticky top-0 z-40 backdrop-blur-lg bg-slate-900 bg-opacity-80 border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center">
                <UtensilsCrossed className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <h1 className="text-lg sm:text-xl md:text-2xl font-bold bg-gradient-to-r from-orange-400 to-red-400 bg-clip-text text-transparent">
                Order History
              </h1>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* User Profile Header */}
        {user && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="food-card"
          >
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                <User size={28} className="text-white" />
              </div>
              <div className="flex-1">
                <h2 className="text-white font-bold text-xl">{user.name}</h2>
                <p className="text-gray-400 text-sm">{user.email}</p>
                {user.customerId && (
                  <p className="text-orange-400 text-xs font-medium">Customer ID: {user.customerId}</p>
                )}
              </div>
              <div className="text-right">
                <p className="text-gray-400 text-sm">Total Orders</p>
                <p className="text-orange-400 font-bold text-2xl">{orders.length}</p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Order Statistics */}
        {orders.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4"
          >
            <div className="bg-slate-800 rounded-lg p-4 text-center">
              <div className="text-orange-400 font-bold text-xl">
                {orders.length}
              </div>
              <div className="text-gray-400 text-sm">Total Orders</div>
            </div>
            
            <div className="bg-slate-800 rounded-lg p-4 text-center">
              <div className="text-green-400 font-bold text-xl">
                {orders.filter(o => o.status === 'delivered').length}
              </div>
              <div className="text-gray-400 text-sm">Completed</div>
            </div>
            
            <div className="bg-slate-800 rounded-lg p-4 text-center">
              <div className="text-blue-400 font-bold text-xl">
                {orders.filter(o => o.status === 'pending' || o.status === 'shipped').length}
              </div>
              <div className="text-gray-400 text-sm">Active</div>
            </div>
            
            <div className="bg-slate-800 rounded-lg p-4 text-center">
              <div className="text-purple-400 font-bold text-xl">
                ${orders.reduce((sum, order) => sum + order.amount, 0).toFixed(0)}
              </div>
              <div className="text-gray-400 text-sm">Total Spent</div>
            </div>
          </motion.div>
        )}

        {/* Orders List */}
        {orders.length > 0 ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-white font-bold text-lg">Your Orders</h3>
              <span className="text-gray-400 text-sm">{orders.length} orders found</span>
            </div>
            {orders.map((order, index) => (
              <OrderItem key={order.id} order={order} index={index} />
            ))}
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="food-card text-center py-12"
          >
            <div className="text-6xl mb-4">📦</div>
            <h3 className="text-2xl font-bold text-white mb-2">No Orders Yet</h3>
            {user && (
              <p className="text-gray-300 mb-2">
                Welcome, <span className="text-orange-400 font-semibold">{user.name}</span>!
              </p>
            )}
            <p className="text-gray-400 mb-6">
              You haven't placed any orders yet. Start shopping to see your order history here.
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setCurrentScreen('customer-home')}
              className="btn-primary py-3 px-8"
            >
              Browse Menu
            </motion.button>
          </motion.div>
        )}
      </div>

      <BottomNavigation />
    </div>
  );
};

export default CustomerHistory;
